import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import './Legal.css';

const Terms = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page-container">
      <div className="legal-content-wrapper">
        
        {/* Navigation Bar */}
        <div className="legal-nav-header">
          <div className="legal-nav-left">
            <button className="legal-back-btn" onClick={() => navigate(-1)} aria-label="Go back">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              <span>BACK</span>
            </button>
            <div className="legal-breadcrumb-badge">
              <span>CRACKIT AI</span>
              <span className="crumb-separator">/</span>
              <span>LEGAL PROTOCOLS</span>
              <span className="crumb-separator">/</span>
              <span className="crumb-current">TERMS OF SERVICE</span>
            </div>
          </div>

          <div className="legal-tabs-switch">
            <Link to="/terms" className="legal-tab-link active-legal-tab">Terms of Service</Link>
            <Link to="/privacy" className="legal-tab-link">Privacy Policy</Link>
            <Link to="/refund-policy" className="legal-tab-link">Refund Policy</Link>
          </div>
        </div>

        {/* Header Banner */}
        <motion.div 
          className="legal-header-card"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <span className="legal-category-badge">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
            Legal Documentation
          </span>
          <h1 className="legal-main-title">TERMS & CONDITIONS</h1>
          <div className="legal-meta-row">
            <span className="legal-meta-pill">
              <strong>Effective:</strong> September 19, 2026
            </span>
            <span className="legal-meta-pill">
              <strong>Platform:</strong> CrackIT AI (Web & Desktop HUD)
            </span>
            <span className="legal-meta-pill">
              <strong>Contact:</strong> support@crackit.si
            </span>
          </div>
        </motion.div>

        {/* Main Document Body */}
        <motion.div 
          className="legal-body-card"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.05 }}
        >
          
          <div className="legal-callout-box callout-info">
            <div className="callout-icon-wrapper">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
            </div>
            <div className="callout-body">
              <h4>Important Agreement Summary</h4>
              <p>
                By using CrackIT AI (including our web platform, desktop stealth HUD overlay, APIs, and credit services), you agree to these legally binding Terms. If you do not agree, please do not use our services.
              </p>
            </div>
          </div>

          <section className="legal-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              These Terms and Conditions ("Terms") constitute a legally binding agreement between you ("User", "you", or "your") and CrackIT AI Technologies Inc. ("CrackIT AI", "we", "us", or "our"), governing your access to and use of the CrackIT AI website, desktop applications, APIs, and related AI interview preparation and live assistance services.
            </p>
            <p>
              By accessing the website at <strong>crackit.ai</strong>, downloading the CrackIT Desktop Client, registering an account, or purchasing credits, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy and Refund Policy.
            </p>
          </section>

          <div className="legal-divider"></div>

          <section className="legal-section">
            <h2>2. Eligibility & Account Security</h2>
            <p>
              To use CrackIT AI, you must be at least 18 years of age (or the legal age of majority in your jurisdiction) and capable of forming a binding contract.
            </p>
            <ul>
              <li><strong>Account Information:</strong> You agree to provide accurate, current, and complete information during registration and keep your account details updated.</li>
              <li><strong>Credential Safeguarding:</strong> You are solely responsible for maintaining the confidentiality of your account authentication tokens and passwords. You agree not to share your account credentials with any third party.</li>
              <li><strong>Account Activity:</strong> You accept full responsibility for all activities, credit usage, and sessions conducted under your authenticated account.</li>
            </ul>
          </section>

          <div className="legal-divider"></div>

          <section className="legal-section">
            <h2>3. Service Description & AI Intelligence Disclaimer</h2>
            <p>
              CrackIT AI provides real-time speech transcription, screen visual OCR analysis, and AI-powered coding and behavioral suggestion generation.
            </p>
            <ul>
              <li><strong>Assistive Nature:</strong> CrackIT AI is designed as a career preparation, mock interview practice, and live cognitive assistance tool. All generated hints, code snippets, algorithms, and suggested answers are produced by third-party Large Language Models (LLMs) including OpenAI GPT-4o, Anthropic Claude, and Google Gemini.</li>
              <li><strong>No Guarantee of Accuracy:</strong> While we optimize for high precision and ultra-low latency, AI-generated content may occasionally contain factual errors, suboptimal code complexity, or hallucinations. Users must exercise independent judgment when evaluating suggestions.</li>
              <li><strong>No Employment Guarantee:</strong> CrackIT AI does not guarantee interview clearance, hiring decisions, compensation offers, or employment outcomes.</li>
            </ul>
          </section>

          <div className="legal-divider"></div>

          <section className="legal-section">
            <h2>4. Acceptable Use Policy & User Conduct</h2>
            <p>
              You agree to use CrackIT AI in a lawful, ethical, and responsible manner. You agree that you will NOT:
            </p>
            <ul>
              <li>Decompile, reverse engineer, disassemble, or attempt to derive the source code of the CrackIT desktop client, bypass hardware overlays, or tamper with credit deduction mechanisms.</li>
              <li>Use the service in violation of applicable national, state, or local laws, company confidentiality obligations, employer non-disclosure agreements (NDAs), or trade secret protections.</li>
              <li>Resell, lease, sublicense, or distribute access to CrackIT AI accounts or API keys to third parties.</li>
              <li>Transmit harmful code, automated scraping bots, viruses, or conduct Denial of Service (DoS) attacks against our infrastructure.</li>
            </ul>
          </section>

          <div className="legal-divider"></div>

          <section className="legal-section">
            <h2>5. Credits, Pricing & Strict No-Refund Policy</h2>
            <p>
              CrackIT AI operates on a pay-as-you-go credit system. Each minute of active live interview audio transcription and OCR consumes utility credits from your account balance.
            </p>
            <div className="legal-callout-box callout-danger">
              <span className="callout-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </span>
              <div className="callout-body">
                <h4>Strict No-Refund Policy</h4>
                <p>
                  <strong>All sales and credit purchases on CrackIT AI are final and non-refundable under all circumstances.</strong> Digital consumable credits and upstream AI compute resources are allocated immediately upon payment. Unused credits do not expire, but cannot be refunded or exchanged for fiat currency. For full details, see our <Link to="/refund-policy">Refund Policy</Link>.
                </p>
              </div>
            </div>
            <ul>
              <li><strong>Zero Recurring Fees:</strong> Credit packages are one-time payments with zero automatic renewals or hidden recurring subscription charges.</li>
              <li><strong>Secure Payment Processing:</strong> Payments are processed via PCI-DSS compliant payment gateways (Razorpay). CrackIT AI never stores credit card details on its servers.</li>
            </ul>
          </section>

          <div className="legal-divider"></div>

          <section className="legal-section">
            <h2>6. Intellectual Property Rights</h2>
            <p>
              The CrackIT AI platform, including its software algorithms, website design, UI/UX components, logos, trademarks, and documentation, is owned exclusively by CrackIT AI Technologies Inc.
            </p>
            <p>
              You retain all ownership rights to any personal resume files and career materials you upload. By uploading materials, you grant CrackIT AI a non-exclusive license strictly to process and display that context for your personal interview sessions.
            </p>
          </section>

          <div className="legal-divider"></div>

          <section className="legal-section">
            <h2>7. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by applicable law, in no event shall CrackIT AI, its directors, employees, partners, or suppliers be liable for any indirect, incidental, special, consequential, or punitive damages (including loss of profits, data, employment opportunities, or goodwill) arising out of or related to your use of or inability to use the service.
            </p>
            <p>
              In all circumstances, CrackIT AI's total aggregate liability shall not exceed the actual amount paid by you to CrackIT AI in the thirty (30) days preceding the event giving rise to the claim.
            </p>
          </section>

          <div className="legal-divider"></div>

          <section className="legal-section">
            <h2>8. Modifications to Terms</h2>
            <p>
              We reserve the right to modify or replace these Terms at any time. Material changes will be posted on this page with an updated "Effective Date". Continued use of the platform following any modifications constitutes your acceptance of the revised Terms.
            </p>
          </section>

          {/* Contact Card */}
          <div className="legal-contact-card">
            <h3>Have Questions About Our Terms?</h3>
            <p>Reach out to our legal and support team directly:</p>
            <a href="mailto:support@crackit.si" className="legal-email-link">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect width="20" height="16" x="2" y="4" rx="2"/>
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
              </svg>
              <span>support@crackit.si</span>
            </a>
          </div>

        </motion.div>

      </div>
    </div>
  );
};

export default Terms;
